/**
 * `/` start page and `/spiel/:id` format page.
 *
 * The start page is the forest clearing: crown and wordmark, one tile per
 * visible game (artwork from `games/<id>/tile.svg`), small objects sitting on
 * the first row of tiles, a few leaves drifting down once. Which games
 * appear is decided by the server (`/api/games`, hidden modules excluded).
 */
import type { GameInfo } from '@schulspiele/protocol';

import { api } from '@shared/api';
import { esc, qs } from '@shared/dom';
import { t } from '@shared/i18n/de';
import { dropLeaves } from '@shared/ui/leaves';
import { gameTile } from '../games';
import acorns from './art/decor-acorns.svg?raw';
import fern from './art/decor-fern.svg?raw';
import mushrooms from './art/decor-mushrooms.svg?raw';
import snail from './art/decor-snail.svg?raw';
import sapling from './art/sapling.svg?raw';
import { guideIdFor } from './guide/sections';
import { forestLayout } from './layout';

/** Decorations for the first row, by tile index. Deterministic so the page looks the same each visit. */
const DECOR: Record<number, { svg: string; side: 'left' | 'right' }> = {
  0: { svg: mushrooms, side: 'left' },
  2: { svg: snail, side: 'right' },
  4: { svg: fern, side: 'left' },
  6: { svg: acorns, side: 'right' },
};

function tileHtml(game: GameInfo, index: number): string {
  const art = gameTile(game.id);
  const decor = DECOR[index];
  const decorHtml =
    decor === undefined
      ? ''
      : `<span class="tile-decor tile-decor--${decor.side}" aria-hidden="true">${decor.svg}</span>`;
  const inner = art ?? `<span>${esc(game.name)}</span>`;
  return `<div class="tile-cell" style="animation-delay:${String(150 + index * 90)}ms">
    ${decorHtml}
    <a class="tile ${art === null ? 'tile--plain' : ''}" href="/spiel/${esc(game.id)}" data-link aria-label="${esc(game.name)}">${inner}</a>
  </div>`;
}

export function renderStart(mount: HTMLElement): void {
  const main = forestLayout(
    mount,
    `<div data-tiles aria-busy="true"></div>
     <p class="join-callout">
       <span>${t('start.playHint')}</span>
       <a class="btn" href="/spielen" data-link>${t('nav.play')}</a>
     </p>`,
  );
  const host = qs(main, '[data-tiles]');
  dropLeaves();

  void api<{ games: GameInfo[] }>('/api/games')
    .then(({ games }) => {
      host.removeAttribute('aria-busy');
      if (games.length === 0) {
        host.innerHTML = `<div class="empty">
          <div class="empty__art" aria-hidden="true">${sapling}</div>
          <h2>${t('start.emptyTitle')}</h2>
          <p class="muted">${t('start.emptyText')}</p>
        </div>`;
        return;
      }
      host.innerHTML = `<div class="tile-grid">${games.map(tileHtml).join('')}</div>`;
    })
    .catch(() => {
      host.innerHTML = `<p class="error">${t('error.generic')}</p>`;
    });
}

export function renderGamePage(mount: HTMLElement, gameId: string): void {
  const main = forestLayout(mount, `<div class="format" data-body aria-busy="true"></div>`);
  const body = qs(main, '[data-body]');

  void api<{ game: GameInfo }>(`/api/games/${encodeURIComponent(gameId)}`)
    .then(({ game }) => {
      body.removeAttribute('aria-busy');
      const art = gameTile(game.id);
      const guide = guideIdFor(game.id);
      body.innerHTML = `
        ${art === null ? '' : `<div class="tile format__tile" aria-hidden="true">${art}</div>`}
        <h1>${esc(game.name)}</h1>
        <p style="font-size:var(--text-lg)">${esc(game.description)}</p>
        <div class="format__facts">
          <span class="status-pill">${game.devicesOptional ? t('format.devicesOptional') : game.needsStudentDevices ? t('format.devices') : t('format.noDevices')}</span>
          ${questionsPill(game)}
          ${game.usesMedia ? `<span class="status-pill">${t('format.media')}</span>` : ''}
        </div>
        <div class="actions">
          <a class="btn btn--big" href="/lehrer?start=${esc(game.id)}" data-link>${t('format.startAsTeacher')}</a>
          ${game.needsStudentDevices ? `<a class="btn btn--big btn--ghost" href="/spielen" data-link>${t('nav.play')}</a>` : ''}
        </div>
        ${guide === null ? '' : `<p><a href="/anleitungen#${esc(guide)}" data-link>So geht ${esc(game.name)}: die Anleitung</a></p>`}`;
    })
    .catch(() => {
      body.innerHTML = `<h1>${t('format.notFound')}</h1><p><a href="/" data-link>${t('nav.home')}</a></p>`;
    });
}

/**
 * Where the questions come from. A format that draws a fixed, large number
 * from the question bank (Klasse gegen den Jäger) says so: a Blitzquiz set
 * is too small for it (U19).
 */
function questionsPill(game: GameInfo): string {
  const fromBank = !game.hasSets && game.bank?.draw != null && !game.bank.optional;
  if (fromBank) return `<span class="status-pill">${t('format.bank')}</span>`;
  if (game.hasSets || game.playsSetsOf.length > 0)
    return `<span class="status-pill">${t('format.sets')}</span>`;
  return '';
}
